import {Component, effect, inject, signal, untracked} from '@angular/core';
import {Location, NgClass} from "@angular/common";
import {DataSubscriptionService} from "src/app/infrastructure/data-subscription.service";
import {GenericButtonComponent} from "src/app/presentation/common/generic-button/generic-button.component";
import {DataSynchronisationService} from "src/app/infrastructure/data-synchronisation.service";
import {ViewportService} from "src/app/infrastructure/viewport.service";
import {ActionType, DataType} from "src/app/domain/domain-models";
import {
  Asset,
  DeviceAssociation,
  SynchronizationStatus,
  WaterPoint,
  WaterPointType
} from "src/app/infrastructure/moovement-api/models/asset";
import {FormsModule} from "@angular/forms";
import {FarmStore} from 'src/app/domain/assets/farm/farm.store';
import {MainViewStore} from 'src/app/presentation/main-view/main-view.store';
import {
  DeviceIdInputComponent,
  DeviceIdSelected
} from "src/app/presentation/common/device-id-input/device-id-input.component";
import {NavBarStateStore} from 'src/app/presentation/main-view/nav-bar/nav-bar-state/nav-bar-state.store';
import {Subject, takeUntil} from 'rxjs';
import {AddWaterPointStore} from 'src/app/presentation/main-view/add-device/add-water-point/add-water-point.store';
import {BootstrapService} from 'src/app/infrastructure/init/bootstrap.service';
import {
  dropAPinMessage,
  InternalNotification,
  InternalNotificationsStore,
  InternalNotificationType
} from 'src/app/infrastructure/internal-notifications/internal-notifications.store';
import {randomUuid} from 'src/app/domain/util/uuid/uuid';
import {ActivatedRoute, Router} from '@angular/router';
import {DeviceStore} from 'src/app/domain/devices/device.store';
import {DeviceAssociationStore} from 'src/app/domain/devices/device-association.store';
import {parseOptionalFloat, parseOptionalInt} from 'src/app/domain/util/parsing/parsing';
import {WaterPointsStore} from 'src/app/domain/assets/water-points/water-points.store';
import {InfoButtonComponent} from "src/app/presentation/common/info-button/info-button.component";
import {
  datumInfo,
  highAlertInfo,
  highWarningInfo,
  lowAlertInfo,
  lowWarningInfo,
  nominalValueInfo
} from 'src/app/domain/assets/water-points/water-point';

export enum Destination {
  WATER_POINTS = "WATER_POINTS"
}

@Component({
  selector: 'app-add-water-point',
  imports: [
    GenericButtonComponent,
    NgClass,
    FormsModule,
    DeviceIdInputComponent,
    InfoButtonComponent
],
  templateUrl: './add-water-point.component.html',
  styleUrl: './add-water-point.component.css'
})
export class AddWaterPointComponent {

  id = signal<string | undefined>(undefined);

  existingDeviceAssociation = signal<DeviceAssociation | undefined>(undefined);
  existingAsset = signal<Asset | undefined>(undefined);

  loading = signal(false);
  validId = signal(false);

  canProcessNewAsset = signal(false);

  nominalValueInfo = signal(nominalValueInfo);
  datumInfo = signal(datumInfo);
  highAlertInfo = signal(highAlertInfo);
  highWarningInfo = signal(highWarningInfo);
  lowWarningInfo = signal(lowWarningInfo);
  lowAlertInfo = signal(lowAlertInfo);

  private destination?: string;

  private $destroyDropAPin: Subject<void> = new Subject<void>();
  private dropAPinNotification?: InternalNotification;

  store = inject(AddWaterPointStore);

  private activatedRoute = inject(ActivatedRoute);
  private deviceAssociationStore = inject(DeviceAssociationStore);
  private deviceStore = inject(DeviceStore);
  private farmStore = inject(FarmStore);
  private internalNotificationsStore = inject(InternalNotificationsStore);
  private location = inject(Location);
  private mainViewStore = inject(MainViewStore);
  private menuStore = inject(NavBarStateStore);
  private router = inject(Router);
  private waterPointsStore = inject(WaterPointsStore);

  constructor(
    private bootstrapService: BootstrapService,
    private dataSubscriptionService: DataSubscriptionService,
    private dataSynchronisationService: DataSynchronisationService,
    protected viewportService: ViewportService
  ) {
    this.activatedRoute.queryParamMap.subscribe(map => {
      const deviceId = map.get('hardwareId');
      if (deviceId) {
        this.store.setId(deviceId);
      }

      const destination = map.get('destination');
      this.destination = this.toDestination(destination);
    });

    effect(() => {
        const id = this.store.id();
        if (!id || this.deviceStore.loading()) return;

        untracked(() => this.processDeviceId(id));
    });

    effect(() => {
      this.canProcessNewAsset.set(
        !this.loading() &&
        this.farmStore.farm() !== undefined &&
        this.store.name() !== undefined &&
        this.store.lat() !== undefined && this.store.long() !== undefined
      );
    });
  }

  private toDestination(destination: string | null) {
    if (destination == Destination.WATER_POINTS) return Destination.WATER_POINTS;

    return undefined;
  }

  back(): void {
    this.location.back();
  }

  close() {
    this.mainViewStore.hideChildComponent();
  }

  onDeviceIdSelected(deviceIdSelected: DeviceIdSelected) {
    const id = deviceIdSelected.id;
    if (!id) return;

    this.store.setId(id);
  }

  processDeviceId(deviceId: string) {
    const devices = this.deviceStore.entities();
    const device = devices.find(device => device.hardwareId?.toLowerCase()?.endsWith(deviceId.toLowerCase()));
    if (!device) {
      this.internalNotificationsStore.addNotification(
        `Water monitor '${deviceId}' is not assigned to your farm.`,
        InternalNotificationType.ERROR,
        false
      );
      this.store.setId(undefined);
      return;
    }

    this.id.set(device.id.slice(-5));

    this.store.setDeviceToMatch(device);
    this.processMatchingDeviceId();
  }

  async processNewAsset() {
    if (!this.canProcessNewAsset()) return;

    this.loading.set(true);

    const waterPoint: WaterPoint = {
      id: randomUuid(),
      version: 0,
      name: this.store.name(),
      location: {
        coords: [this.store.lat()!, this.store.long()!]
      },
      farmId: this.farmStore.farm()!.id,
      type: WaterPointType.UNKNOWN,
      synchronizationStatus: SynchronizationStatus.creationPending
    }

    try {
      await this.dataSynchronisationService.applyAction(
        DataType.waterPoints,
        ActionType.createAsset,
        waterPoint,
        SynchronizationStatus.creationPending
      );

      await this.dataSynchronisationService.applyAction(
        DataType.deviceAssociations,
        ActionType.mergeAsset,
        {
          id: this.store.deviceToMatch()?.id! + waterPoint.id,
          version: 0,
          startDateTime: new Date().toISOString(),
          assetId: waterPoint.id,
          deviceId: this.store.deviceToMatch()?.id,
          sampleThresholds: {
            WaterLevel: {
              thresholds: {
                HIGH_ALERT: this.store.highAlertThreshold(),
                HIGH_WARNING: this.store.highWarningThreshold(),
                LOW_WARNING: this.store.lowWarningThreshold(),
                LOW_ALERT: this.store.lowAlertThreshold()
              },
              datum: this.store.datum(),
              nominalValue: this.store.nominalValue()
            }
          },
          synchronizationStatus: SynchronizationStatus.syncPending
        },
        SynchronizationStatus.syncPending
      );

      this.bootstrapService.reloadWaterPoints();

      this.internalNotificationsStore.addNotification("Successfully added water point.", InternalNotificationType.SUCCESS, false);

      if (this.destination === Destination.WATER_POINTS) {
        this.router.navigate(['/main/water-points']);
      }

      this.reset()
    } catch (error) {
      this.internalNotificationsStore.addNotification(
        "Unable to add water point.",
        InternalNotificationType.ERROR,
        true
      );
      this.loading.set(false);
    }
  }

  private processMatchingDeviceId() {
    const deviceId = this.store.deviceToMatch()?.id;
    if (!deviceId) {
      this.initializeForm();
      return;
    }

    const deviceAssociation = this.deviceAssociationStore.deviceIdToAssociation().get(deviceId);
    if (!deviceAssociation) {
      this.initializeForm();
      return;
    }

    this.existingDeviceAssociation.set(deviceAssociation);
    this.existingAsset.set(this.waterPointsStore.entityMap()[deviceAssociation.assetId]);
  }

  setName(event: Event) {
    if (!event.target) return;

    const string = (event.target as HTMLInputElement).value;
    this.store.setName(string === '' ? undefined : string);
  }

  setLat(event: Event) {
    if (!event.target) return;

    const string = (event.target as HTMLInputElement).value;
    this.store.setLat(parseOptionalFloat(string));
  }

  setLong(event: Event) {
    if (!event.target) return;

    const string = (event.target as HTMLInputElement).value;
    this.store.setLong(parseOptionalFloat(string));
  }

  setNominalValue(event: Event) {
    if (!event.target) return;

    const string = (event.target as HTMLInputElement).value;
    this.store.setNominalValue(parseOptionalInt(string));
  }

  setDatum(event: Event) {
    if (!event.target) return;

    const string = (event.target as HTMLInputElement).value;
    this.store.setDatum(parseOptionalInt(string));
  }

  setHighAlertThreshold(event: Event) {
    if (!event.target) return;

    const string = (event.target as HTMLInputElement).value;
    this.store.setHighAlertThreshold(parseOptionalInt(string));
  }

  setHighWarningThreshold(event: Event) {
    if (!event.target) return;

    const string = (event.target as HTMLInputElement).value;
    this.store.setHighWarningThreshold(parseOptionalInt(string));
  }

  setLowWarningThreshold(event: Event) {
    if (!event.target) return;

    const string = (event.target as HTMLInputElement).value;
    this.store.setLowWarningThreshold(parseOptionalInt(string));
  }

  setLowAlertThreshold(event: Event) {
    if (!event.target) return;

    const string = (event.target as HTMLInputElement).value;
    this.store.setLowAlertThreshold(parseOptionalInt(string));
  }

  private initializeForm() {
    const farm = this.farmStore.farm();
    if (!farm || !farm.location || !farm.location.coords || !farm.location.coords[0] || !farm.location.coords[1]) return;

    if (this.store.lat() !== undefined && this.store.long() !== undefined) return;

    this.store.setLat(farm.location.coords[0]);
    this.store.setLong(farm.location.coords[1]);
  }

  async deleteDeviceAssociation() {
    const association = this.existingDeviceAssociation();
    if (!association) return;

    const asset = this.existingAsset();
    if (asset) {
      await this.dataSynchronisationService.dissociateAsset(asset, DataType.waterPoints);
    } else {
      await this.dataSynchronisationService.dissociateDeviceAssociation(association);
    }

    this.internalNotificationsStore.addNotification("Successfully deleted asset.", InternalNotificationType.SUCCESS, false);

    this.bootstrapService.reloadWaterPoints();

    this.reset();
  }

  reset() {
    this.loading.set(false);
    this.existingDeviceAssociation.set(undefined);
    this.existingAsset.set(undefined);
    this.store.reset();
  }

  onDropAPin() {
    this.menuStore.disable();
    this.dropAPinNotification = this.internalNotificationsStore.addNotification(dropAPinMessage, InternalNotificationType.INFO, true);
    this.dataSubscriptionService.publish('dropAPin', true)
    this.dataSubscriptionService.onFirst<{
      lng: number,
      lat: number
    }>('currentPosition').pipe(takeUntil(this.$destroyDropAPin)).subscribe(data => {
      if (confirm('Are you sure you want to use this location for this water point?')) {
        this.store.setLat(data.lat);
        this.store.setLong(data.lng);
      }
      this.menuStore.enable();
      this.$destroyDropAPin.complete();
      this.$destroyDropAPin = new Subject<void>();
      this.dataSubscriptionService.publish('currentPosition');
      if (this.dropAPinNotification) this.internalNotificationsStore.deleteNotification(this.dropAPinNotification);
      this.dataSubscriptionService.publish('dropAPin', false);
      this.mainViewStore.showChildComponent();
    });
    this.mainViewStore.hideChildComponent();
  }
}
